import React from 'react';
import {
  Compass,
  XCircle,
  Wallet,
  BadgeCheck,
  Ban,
  ListOrdered,
  CreditCard,
  Mail,
} from 'lucide-react';
import '../../../../styles/components/privacy-policy.scss';
import HeaderBanner from '../../../common/HeaderBanner';
import i18n from '../../../../i18n';

/*
 * i18n.t(key, 'Default text') — the English default shows until you add the key
 * to your translation files, so the page works immediately.
 */
class RefundPolicyPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      activeSection: 'overview',
    };
    this.observer = null;
    this.clickLock = false;
    this.lockTimer = null;
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    this.setupScrollSpy();
  }

  componentWillUnmount() {
    if (this.observer) this.observer.disconnect();
    clearTimeout(this.lockTimer);
  }

  // Highlights the sidebar item for the section currently on screen
  setupScrollSpy = () => {
    if (!('IntersectionObserver' in window)) return;
    this.observer = new IntersectionObserver(
      (entries) => {
        if (this.clickLock) return;
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length) this.setState({ activeSection: visible[0].target.id });
      },
      { rootMargin: '-120px 0px -60% 0px', threshold: 0 }
    );
    document.querySelectorAll('.privacy-section').forEach((el) => this.observer.observe(el));
  };

  handleSectionClick = (sectionId) => {
    this.clickLock = true;
    this.setState({ activeSection: sectionId });
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    clearTimeout(this.lockTimer);
    this.lockTimer = setTimeout(() => {
      this.clickLock = false;
    }, 800);
  };

  render() {
    const { activeSection } = this.state;

    const menuItems = [
      { id: 'overview', label: i18n.t('rp_nav_overview', 'Overview'), icon: Compass },
      { id: 'cancellation', label: i18n.t('rp_nav_cancellation', 'Cancellation'), icon: XCircle },
      { id: 'deposit-refunds', label: i18n.t('rp_nav_deposits', 'Deposit Refunds'), icon: Wallet },
      { id: 'returns', label: i18n.t('rp_nav_returns', 'Returns'), icon: BadgeCheck },
      {
        id: 'non-returnable',
        label: i18n.t('rp_nav_nonreturnable', 'Non-returnable Items'),
        icon: Ban,
      },
      {
        id: 'refund-process',
        label: i18n.t('rp_nav_process', 'How to Request a Refund'),
        icon: ListOrdered,
      },
      {
        id: 'refund-method',
        label: i18n.t('rp_nav_method', 'Refund Method & Timeline'),
        icon: CreditCard,
      },
      { id: 'contact-us', label: i18n.t('contact_us'), icon: Mail },
    ];

    return (
      <div className="privacy-page">
        <HeaderBanner
          title={i18n.t('refund_policy', 'Cancellation, Refund & Return Policy')}
          subtitle={i18n.t(
            'refund_policy_subtitle',
            'When you can cancel, how deposits are returned and how refunds work.'
          )}
        />

        <div className="privacy-container">
          <aside className="privacy-sidebar">
            <nav>
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => this.handleSectionClick(item.id)}
                    className={`sidebar-item ${isActive ? 'active' : ''}`}
                    aria-current={isActive ? 'true' : undefined}
                  >
                    <Icon size={20} />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </nav>
          </aside>

          <main className="privacy-content">
            <div className="update-date">{i18n.t('last_updated')}: 27 September 2026</div>

            <div className="intro-text">
              {i18n.t(
                'rp_intro',
                'Because auctions are competitive and bids are binding, cancellations and returns are limited. This policy explains exactly when they apply and how refunds are processed.'
              )}
            </div>

            {/* 1. Overview */}
            <section id="overview" className="privacy-section">
              <h2>{i18n.t('rp_s1_title', '1. Overview')}</h2>
              <p>
                {i18n.t(
                  'rp_s1_p1',
                  'This policy applies to all purchases, deposits and fees paid through the Mzadcom platform, and should be read together with our Terms & Conditions.'
                )}
              </p>
            </section>

            {/* 2. Cancellation */}
            <section id="cancellation" className="privacy-section">
              <h2>{i18n.t('rp_s2_title', '2. Cancellation of Bids & Orders')}</h2>
              <p>
                {i18n.t(
                  'rp_s2_p1',
                  'A bid is a binding commitment and cannot be cancelled by the bidder once placed. Cancellation may, however, occur in the following cases:'
                )}
              </p>
              <ul>
                <li>
                  {i18n.t(
                    'rp_s2_l1',
                    'Mzadcom cancels an auction due to a technical error, seller withdrawal or legal requirement — any amounts paid will be refunded in full.'
                  )}
                </li>
                <li>
                  {i18n.t(
                    'rp_s2_l2',
                    'A bid was placed in clear error (for example, an obvious typing mistake) and you contact us before the auction closes — approval is at our discretion.'
                  )}
                </li>
                <li>
                  {i18n.t(
                    'rp_s2_l3',
                    'The winning bidder fails to pay within the required period — the sale is cancelled and the participation deposit may be forfeited.'
                  )}
                </li>
              </ul>
            </section>

            {/* 3. Deposits */}
            <section id="deposit-refunds" className="privacy-section">
              <h2>{i18n.t('rp_s3_title', '3. Participation Deposit Refunds')}</h2>
              <ul>
                <li>
                  {i18n.t(
                    'rp_s3_l1',
                    'If you do not win the auction, your deposit is refunded automatically after the auction closes.'
                  )}
                </li>
                <li>
                  {i18n.t(
                    'rp_s3_l2',
                    'If you win, your deposit is deducted from the final amount payable.'
                  )}
                </li>
                <li>
                  {i18n.t(
                    'rp_s3_l3',
                    'If you win but do not complete payment, the deposit is non-refundable.'
                  )}
                </li>
              </ul>
            </section>

            {/* 4. Returns */}
            <section id="returns" className="privacy-section">
              <h2>{i18n.t('rp_s4_title', '4. Returns')}</h2>
              <p>
                {i18n.t(
                  'rp_s4_p1',
                  'Items are generally sold "as is". A return may be accepted only where:'
                )}
              </p>
              <ul>
                <li>
                  {i18n.t(
                    'rp_s4_l1',
                    'The item received is materially different from the auction description.'
                  )}
                </li>
                <li>
                  {i18n.t(
                    'rp_s4_l2',
                    'The item was damaged before handover and this was recorded at the time of collection or delivery.'
                  )}
                </li>
                <li>
                  {i18n.t(
                    'rp_s4_l3',
                    'The return request is submitted within 48 hours of collection or delivery, with supporting photos.'
                  )}
                </li>
              </ul>
            </section>

            {/* 5. Non-returnable */}
            <section id="non-returnable" className="privacy-section">
              <h2>{i18n.t('rp_s5_title', '5. Non-returnable Items')}</h2>
              <ul>
                <li>
                  {i18n.t(
                    'rp_s5_l1',
                    'Items whose condition or defects were disclosed in the auction description.'
                  )}
                </li>
                <li>
                  {i18n.t(
                    'rp_s5_l2',
                    'Vehicles, real estate and other items whose ownership has already been transferred through official procedures.'
                  )}
                </li>
                <li>
                  {i18n.t(
                    'rp_s5_l3',
                    'Items that have been used, altered or damaged after handover.'
                  )}
                </li>
              </ul>
            </section>

            {/* 6. Process */}
            <section id="refund-process" className="privacy-section">
              <h2>{i18n.t('rp_s6_title', '6. How to Request a Refund')}</h2>
              <ol>
                <li>
                  {i18n.t(
                    'rp_s6_l1',
                    'Contact our customer service team by email or phone with your auction number and the reason for the request.'
                  )}
                </li>
                <li>
                  {i18n.t(
                    'rp_s6_l2',
                    'Provide supporting evidence such as photographs and the handover document.'
                  )}
                </li>
                <li>
                  {i18n.t(
                    'rp_s6_l3',
                    'Our team reviews the request and replies within 3 working days.'
                  )}
                </li>
                <li>
                  {i18n.t(
                    'rp_s6_l4',
                    'If approved, the refund is issued to your original payment method.'
                  )}
                </li>
              </ol>
            </section>

            {/* 7. Method */}
            <section id="refund-method" className="privacy-section">
              <h2>{i18n.t('rp_s7_title', '7. Refund Method & Timeline')}</h2>
              <p>
                {i18n.t(
                  'rp_s7_p1',
                  'Approved refunds are processed to the original payment method used for the transaction. We do not issue refunds in cash or to a different card.'
                )}
              </p>
              <p>
                {i18n.t(
                  'rp_s7_p2',
                  'Refunds are initiated within 7 working days of approval. Depending on your bank, it may take a further 5–14 working days for the amount to appear on your statement.'
                )}
              </p>
            </section>

            {/* 8. Contact */}
            <section id="contact-us" className="privacy-section">
              <h2>{i18n.t('rp_s8_title', '8. Contact Us')}</h2>
              <p>
                {i18n.t('rp_s8_p1', 'For cancellations, refunds or returns, please contact us:')}
              </p>
              <div className="contact-box">
                <p>
                  <strong>{i18n.t('group_auction')}</strong>
                </p>
                <p>{i18n.t('office_address_info')}</p>
                <p>
                  Email: <a href="mailto:info@mzadcom.om">info@mzadcom.om</a>
                </p>
              </div>
            </section>
          </main>
        </div>
      </div>
    );
  }
}

export default RefundPolicyPage;
